import { useId } from "react";
import {
    COMBO_AXIS_OPTIONS,
    COMBO_MIN_SERIES,
    COMBO_SERIES_TYPE_OPTIONS,
    comboSeriesColor,
    makeComboSeries,
    makeDefaultComboChartSettings,
    moveComboSeries,
    normalizeComboChartSettings,
    orderedComboSeries,
    renumberComboSeries,
} from "../../logic/chartCombo";
import type {
    ExcelChartAxisKey,
    ExcelChartAxisSettings,
    ExcelChartLayout,
    ExcelChartValueFormat,
    ExcelComboChartSettings,
    ExcelComboSeriesSettings,
    ExcelComboSeriesType,
} from "../../types";
import { ReportConfigSelect } from "../ReportConfigSelect";

type DataLabelChoice = "inherit" | "show" | "hide";

const DATA_LABEL_OPTIONS: { value: DataLabelChoice; label: string }[] = [
    { value: "inherit", label: "Chart default" },
    { value: "show", label: "Show" },
    { value: "hide", label: "Hide" },
];

const AXIS_FORMAT_OPTIONS: { value: ExcelChartValueFormat; label: string }[] = [
    { value: "general", label: "Match the series" },
    { value: "accountingInt", label: "Accounting (whole number)" },
    { value: "integer", label: "Whole number" },
    { value: "percent0", label: "Percent (0 decimals)" },
    { value: "percent2", label: "Percent (2 decimals)" },
    { value: "decimal2", label: "Number (2 decimals)" },
];

interface ComboChartEditorProps {
    chart: ExcelChartLayout;
    // Row labels the block is expected to produce (see comboSeriesSuggestions).
    suggestions: string[];
    disabled?: boolean;
    onChange: (chart: ExcelChartLayout) => void;
}

function optionalNumber(value: string) {
    if (!value.trim()) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function dataLabelChoice(series: ExcelComboSeriesSettings): DataLabelChoice {
    if (series.showDataLabels === true) return "show";
    if (series.showDataLabels === false) return "hide";
    return "inherit";
}

export function ComboChartEditor({ chart, suggestions, disabled = false, onChange }: ComboChartEditorProps) {
    const listId = useId();
    const combo: ExcelComboChartSettings = chart.combo ? normalizeComboChartSettings(chart.combo) : makeDefaultComboChartSettings();
    const series = orderedComboSeries(combo);
    const listedKeys = new Set(series.map((item) => item.seriesKey.trim().toLowerCase()).filter(Boolean));
    const unlistedSuggestions = suggestions.filter((label) => !listedKeys.has(label.trim().toLowerCase()));
    const usesSecondary = series.some((item) => item.axis === "secondary");
    const palette = chart.settings?.palette;

    function updateCombo(next: ExcelComboChartSettings) {
        onChange({ ...chart, combo: next });
    }

    function updateSeries(position: number, patch: Partial<ExcelComboSeriesSettings>) {
        updateCombo({ ...combo, series: renumberComboSeries(series.map((item, index) => (index === position ? { ...item, ...patch } : item))) });
    }

    function removeSeries(position: number) {
        updateCombo({ ...combo, series: renumberComboSeries(series.filter((_, index) => index !== position)) });
    }

    function addSeries(labels: string[]) {
        const additions = labels.map((label, offset) => makeComboSeries(label, series.length + offset));
        updateCombo({ ...combo, series: renumberComboSeries([...series, ...additions]) });
    }

    function updateAxis(patch: Partial<ExcelChartAxisSettings>) {
        updateCombo({ ...combo, secondaryAxis: { ...combo.secondaryAxis, ...patch } });
    }

    return (
        <section className="rc-chart-settings-group rc-combo-editor" aria-label="Combo chart series">
            <div className="rc-chart-settings-heading rc-combo-heading">
                <span>
                    <b>Series</b>
                    <small>
                        Each listed row is drawn as a column or a line, on the left or right axis. Only rows listed here are plotted;
                        use the right axis for values on a different scale, such as a rate next to a count.
                    </small>
                </span>
                <span className="rc-combo-heading-actions">
                    {unlistedSuggestions.length > 0 && (
                        <button className="rc-excel-action" type="button" disabled={disabled} onClick={() => addSeries(unlistedSuggestions)} title="Add every suggested row that is not listed yet">
                            <span className="material-icons md-16" aria-hidden="true">playlist_add</span>
                            <span>Add suggested rows</span>
                        </button>
                    )}
                    <button className="rc-excel-action" type="button" disabled={disabled} onClick={() => addSeries([unlistedSuggestions[0] || ""])}>
                        <span className="material-icons md-16" aria-hidden="true">add</span>
                        <span>Add series</span>
                    </button>
                </span>
            </div>

            <datalist id={listId}>
                {suggestions.map((label) => <option key={label} value={label} />)}
            </datalist>

            {series.length === 0 ? (
                <p className="rc-hint rc-combo-empty">No series yet. A combo chart needs at least {COMBO_MIN_SERIES} rows - add them above.</p>
            ) : (
                <div className="rc-combo-table" role="table" aria-label="Combo series settings">
                    <div className="rc-combo-row rc-combo-row-head" role="row">
                        <span role="columnheader">Order</span>
                        <span role="columnheader">Row label</span>
                        <span role="columnheader">Draw as</span>
                        <span role="columnheader">Axis</span>
                        <span role="columnheader">Color</span>
                        <span role="columnheader">Legend name</span>
                        <span role="columnheader">Data labels</span>
                        <span role="columnheader" className="sr-only">Remove</span>
                    </div>
                    {series.map((item, position) => {
                        const color = comboSeriesColor(item, position, palette);
                        const rowName = item.seriesKey.trim() || `Series ${position + 1}`;
                        return (
                            <div className="rc-combo-row" role="row" key={position}>
                                <span className="rc-combo-order" role="cell">
                                    <button className="rc-excel-icon-action" type="button" aria-label={`Move ${rowName} up`} title="Move up" disabled={disabled || position === 0} onClick={() => updateCombo(moveComboSeries(combo, position, -1))}>
                                        <span className="material-icons md-16" aria-hidden="true">arrow_upward</span>
                                    </button>
                                    <button className="rc-excel-icon-action" type="button" aria-label={`Move ${rowName} down`} title="Move down" disabled={disabled || position === series.length - 1} onClick={() => updateCombo(moveComboSeries(combo, position, 1))}>
                                        <span className="material-icons md-16" aria-hidden="true">arrow_downward</span>
                                    </button>
                                </span>
                                <span role="cell">
                                    <input
                                        list={listId}
                                        value={item.seriesKey}
                                        maxLength={255}
                                        placeholder="Row label, e.g. Completed"
                                        aria-label={`Row label for series ${position + 1}`}
                                        disabled={disabled}
                                        onChange={(event) => updateSeries(position, { seriesKey: event.target.value })}
                                    />
                                </span>
                                <span role="cell">
                                    <ReportConfigSelect<ExcelComboSeriesType>
                                        value={item.chartType}
                                        options={COMBO_SERIES_TYPE_OPTIONS}
                                        ariaLabel={`Chart type for ${rowName}`}
                                        disabled={disabled}
                                        onChange={(chartType) => updateSeries(position, { chartType })}
                                    />
                                </span>
                                <span role="cell">
                                    <ReportConfigSelect<ExcelChartAxisKey>
                                        value={item.axis}
                                        options={COMBO_AXIS_OPTIONS}
                                        ariaLabel={`Axis for ${rowName}`}
                                        disabled={disabled}
                                        onChange={(axis) => updateSeries(position, { axis })}
                                    />
                                </span>
                                <span className="rc-combo-color" role="cell">
                                    <input
                                        type="color"
                                        value={`#${color}`}
                                        aria-label={`Color for ${rowName}`}
                                        disabled={disabled}
                                        onChange={(event) => updateSeries(position, { color: event.target.value.replace(/^#/, "").toUpperCase() })}
                                    />
                                    {item.color ? (
                                        <button className="rc-excel-icon-action" type="button" aria-label={`Use palette color for ${rowName}`} title="Use palette color" disabled={disabled} onClick={() => updateSeries(position, { color: null })}>
                                            <span className="material-icons md-16" aria-hidden="true">format_color_reset</span>
                                        </button>
                                    ) : (
                                        <small>Auto</small>
                                    )}
                                </span>
                                <span role="cell">
                                    <input
                                        value={item.label || ""}
                                        maxLength={255}
                                        placeholder="Same as row label"
                                        aria-label={`Legend name for ${rowName}`}
                                        disabled={disabled}
                                        onChange={(event) => updateSeries(position, { label: event.target.value })}
                                    />
                                </span>
                                <span role="cell">
                                    <ReportConfigSelect<DataLabelChoice>
                                        value={dataLabelChoice(item)}
                                        options={DATA_LABEL_OPTIONS}
                                        ariaLabel={`Data labels for ${rowName}`}
                                        disabled={disabled}
                                        onChange={(choice) => updateSeries(position, { showDataLabels: choice === "inherit" ? null : choice === "show" })}
                                    />
                                </span>
                                <span role="cell">
                                    <button className="rc-excel-icon-action danger" type="button" aria-label={`Remove ${rowName}`} title="Remove series" disabled={disabled} onClick={() => removeSeries(position)}>
                                        <span className="material-icons md-16" aria-hidden="true">delete_outline</span>
                                    </button>
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {usesSecondary && (
                <div className="rc-combo-axis">
                    <div className="rc-chart-advanced-section-heading">
                        <b>Right axis</b>
                        <small>Applies to every series drawn on the secondary axis. The left axis uses the options under "Customize this chart".</small>
                    </div>
                    <div className="rc-chart-advanced-grid">
                        <label className="rc-label-block">
                            Axis label <small className="rc-chart-technical-label">Secondary Y-axis title</small>
                            <input value={combo.secondaryAxis.title} maxLength={255} placeholder="Example: On-time rate" disabled={disabled} onChange={(event) => updateAxis({ title: event.target.value })} />
                        </label>
                        <label className="rc-label-block">
                            Value format <small className="rc-chart-technical-label">Secondary Y-axis number format</small>
                            <ReportConfigSelect<ExcelChartValueFormat>
                                value={combo.secondaryAxis.numberFormat}
                                options={AXIS_FORMAT_OPTIONS}
                                ariaLabel="Secondary axis number format"
                                disabled={disabled}
                                onChange={(numberFormat) => updateAxis({ numberFormat })}
                            />
                        </label>
                        <label className="rc-label-block">
                            Start value <small className="rc-chart-technical-label">Secondary Y-axis minimum</small>
                            <input type="number" value={combo.secondaryAxis.minimum ?? ""} placeholder="Automatic" disabled={disabled} onChange={(event) => updateAxis({ minimum: optionalNumber(event.target.value) })} />
                        </label>
                        <label className="rc-label-block">
                            End value <small className="rc-chart-technical-label">Secondary Y-axis maximum</small>
                            <input type="number" value={combo.secondaryAxis.maximum ?? ""} placeholder="Automatic" disabled={disabled} onChange={(event) => updateAxis({ maximum: optionalNumber(event.target.value) })} />
                        </label>
                    </div>
                    <small className="rc-field-note">Percent values are stored as fractions, so a 0-100% axis is 0 to 1.</small>
                </div>
            )}
        </section>
    );
}
